import React, { useState, useEffect, useContext } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import AppContext from "../context/AppContext";
import {
  addFixture,
  editFixture,
  zoneFixtureList,
  removeFixtureDataByID,
} from "../_slice/fixtureSlice";
import {
  getFixture,
  courseList,
  setClearCourseData,
  updateZoneFixturesCount,
} from "../_slice/courseSlice";
import primaryImg from "../../assets/images/primaryExit.png";
import deleteFixture from "../../assets/images/delete1.png";
import { v4 as uuidv4 } from "uuid";
import { useLocation, useNavigate } from "react-router-dom";

const FixtureModal = ({
  show,
  onHide,
  fixtureData,
  zoneId,
  mode,
  ZoneAssembly,
}) => {
  // console.log("FixtureModal ZoneAssembly", ZoneAssembly);

  // State initialization
  const [formData, setFormData] = useState({
    course_id: zoneId,
    fixtures: [],
    delete_ids: [],
  });
  const [totalFixtureCount, setTotalFixtureCount] = useState(0);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const { setLoader } = useContext(AppContext);
  const dispatch = useDispatch();
  const location = useLocation();

  let { courseidFixtureList } = useSelector((state) => state.fixture);

  const locationId = location?.state?.orglocation?.locationId || "";
  // console.log("locationId", locationId);

  // Function to reset form data and errors
  const resetForm = () => {
    setFormData({
      course_id: zoneId,
      fixtures: [],
      delete_ids: [],
    });
    setErrors({});
    setTotalFixtureCount(0);
  };

  // Effect to fetch fixture list when in edit mode or reset form on mode change
  useEffect(() => {
    if (mode === "edit") {
      dispatch(zoneFixtureList({ navigate, setLoader, zoneId })); // Fetch fixtures for the zone in edit mode
    } else {
      resetForm(); // Reset form when switching to add mode
    }
  }, [mode, dispatch, setLoader, zoneId]);

  // Effect to populate form data when courseidFixtureList updates
  useEffect(() => {
    if (mode === "edit") {
      // Initialize delete_ids with existing values if formData already has some
      const deleteIds =
        formData.delete_ids.length > 0 ? [...formData.delete_ids] : [];

      const normalizedData = {
        course_id: courseidFixtureList[0]?.course_id || zoneId,
        fixtures: courseidFixtureList.map((fixture) => ({
          id: fixture.id,
          fixture_id: fixture.fixture_id,
          reference_name: fixture.reference_name,
          serial_number: fixture.serial_number,
          unique_key: uuidv4(),
        })),
        delete_ids: deleteIds,
      };
      setFormData(normalizedData); // Set form data from fetched fixtures
      setTotalFixtureCount(normalizedData.fixtures.length); // Set total fixture count
    }
  }, [courseidFixtureList, mode, zoneId]);

  // Function to remove a fixture
  const removeFixtureData = (id) => {
    dispatch(removeFixtureDataByID(id));
    setFormData((prevData) => {
      return {
        ...prevData,
        delete_ids: [...prevData.delete_ids, id],
      };
    });
    // Update the total fixture count
    // setTotalFixtureCount((prevCount) => prevCount - 1);
    // );
  };

  const handleInputChange = (selectedFixtureData, count) => {
    // List of fixture IDs that should have a max count of 1
    const maxCountFixtureIds = [1, 3, 4];

    // Check if the selected fixture ID is in the list and limit the count to 1 if so
    if (maxCountFixtureIds.includes(selectedFixtureData.id)) {
      count = Math.min(1, count); // Ensure the count does not exceed 1
    } else {
      count = Math.max(0, count); // Ensure count is not negative for other fixtures
    }

    setFormData((prevData) => {
      const existingFixtures = prevData.fixtures.filter(
        (fixture) => fixture.fixture_id === selectedFixtureData.id
      );

      const existingFixtureCount = existingFixtures.length;

      if (count > existingFixtureCount) {
        // Add new fixtures if count is greater than existing fixtures
        const newFixtures = Array.from(
          { length: count - existingFixtureCount },
          () => ({
            fixture_id: selectedFixtureData.id,
            reference_name: "",
            serial_number: "",
            unique_key: uuidv4(),
          })
        );

        return {
          ...prevData,
          fixtures: [...prevData.fixtures, ...newFixtures],
        };
      } else if (count < existingFixtureCount) {
        // Remove fixtures if count is less than existing fixtures
        // const fixturesWithId = existingFixtures.filter((fixture) => fixture.id);
        const fixturesWithoutId = existingFixtures.filter(
          (fixture) => !fixture.id
        );

        // Calculate how many fixtures to remove
        const fixturesToRemoveCount = existingFixtureCount - count;

        // Remove fixtures only from those without `fixture.id`
        const fixturesToRemove = fixturesWithoutId.slice(
          0,
          fixturesToRemoveCount
        );

        return {
          ...prevData,
          fixtures: prevData.fixtures.filter(
            (fixture) => !fixturesToRemove.includes(fixture)
          ),
        };
      }

      // If count equals existing fixtures count, return previous state
      return prevData;
    });

    // Update the total fixture count if not in edit mode
    if (mode !== "edit") {
      setTotalFixtureCount(count);
    }
  };

  // Function to handle changes in fixture inputs
  const handleFixtureChange = (uniqueKey, key, value) => {
    setFormData((prevData) => {
      const newZoneFixtures = prevData.fixtures.map((fixture) =>
        fixture.unique_key === uniqueKey
          ? { ...fixture, [key]: value }
          : fixture
      );

      // Validate the updated fixture field
      // Error Code
      // validateFixtureField(newZoneFixtures, key);

      return {
        ...prevData,
        fixtures: newZoneFixtures,
      };
    });

    // Error Code
    // Clear the error message if the field is valid
    // clearError(uniqueKey, key);
  };

  // Function to validate fixture fields
  const validateFixtureField = (fixtures, key) => {
    // Error Code
    // const newErrors = { ...errors };
    // fixtures.forEach((fixture) => {
    //   const errorMessage =
    //     fixture[key].trim() === "" ? `${key} is required` : "";
    //   newErrors[`${fixture.fixture_id}-${fixture.unique_key}-${key}`] =
    //     errorMessage;
    // });
    // setErrors(newErrors);
  };

  // Function to handle form submission
  const onSubmit = (e) => {
    e.preventDefault();

    // Set DefaultFixture Value August
    const defaultFixture = {
      fixture_id: 3,
      reference_name: "",
      serial_number: "",
    };

    const newErrors = {};
    let hasErrors = false;

    formData.fixtures.forEach((fixture) => {
      const { fixture_id, reference_name, serial_number, unique_key } = fixture;
      const referenceError = "";
      const serialError = "";

      if (referenceError || serialError) {
        hasErrors = true;
      }

      newErrors[`${fixture_id}-${unique_key}-reference_name`] = referenceError;
      newErrors[`${fixture_id}-${unique_key}-serial_number`] = serialError;
    });

    // Error Code
    // formData.fixtures.forEach((fixture) => {
    //   const { fixture_id, reference_name, serial_number, unique_key } = fixture;
    //   const referenceError =
    //     reference_name.trim() === "" ? "Reference Name is required" : "";
    //   const serialError =
    //     serial_number.trim() === "" ? "Serial Number is required" : "";

    //   if (referenceError || serialError) {
    //     hasErrors = true;
    //   }

    //   newErrors[`${fixture_id}-${unique_key}-reference_name`] = referenceError;
    //   newErrors[`${fixture_id}-${unique_key}-serial_number`] = serialError;
    // });

    setErrors(newErrors);

    if (!hasErrors) {
      // console.log('onSubmit ZoneAssembly', ZoneAssembly);

      // Check if there's any fixture with fixture_id of 1, 2, or 4
      const hasRequiredFixtures = formData.fixtures.some((fixture) =>
        [1, 2, 4].includes(fixture.fixture_id)
      );

      // Check if fixture with fixture_id 3 already exists
      const hasFixtureId3 = formData.fixtures.some(
        (fixture) => fixture.fixture_id === 3
      );

      // Conditionally add defaultFixture based on ZoneAssembly and if fixture_id: 3 is not already present
      // const fixturesToSubmit = (hasRequiredFixtures || ZoneAssembly) && !hasFixtureId3
      //   ? [...formData.fixtures, defaultFixture] // Add default fixture if ZoneAssembly exists and fixture_id: 3 is not present
      //   : formData.fixtures;                    // Don't add default fixture if fixture_id: 3 already exists

      // Conditionally add defaultFixture based on ZoneAssembly Only Add
      // const fixturesToSubmit = mode === "add"
      //   ? (ZoneAssembly ? [...formData.fixtures, defaultFixture] : formData.fixtures)
      //   : formData.fixtures;                   // Don't add default fixture if ZoneAssembly is undefined

      // Conditionally add defaultFixture based on ZoneAssembly Only Add / Edit Both
      // In this By default send value Assembly value
      // 18 OCT
      // const fixturesToSubmit = ZoneAssembly
      //   ? [...formData.fixtures, defaultFixture]
      //   : formData.fixtures;

      const fixturesToSubmit = ZoneAssembly
        ? [...formData.fixtures]
        : formData.fixtures;

      // Set In Add Time Value August
      // const fixturesToSubmit = mode === "add"
      //   ? [...formData.fixtures, defaultFixture]
      //   : formData.fixtures;

      const submissionData = {
        course_id: zoneId,
        fixtures: fixturesToSubmit.map((fixture) => {
          return {
            ...(mode === "edit" ? { id: fixture.id } : {}),
            fixture_id: fixture.fixture_id,
            reference_name: fixture.reference_name,
            serial_number: fixture.serial_number,
          };
        }),
        delete_ids: formData.delete_ids,
      };

      // console.log('submissionData', submissionData);
      // return

      if (mode === "add") {
        // console.log('ADDD Mode');

        dispatch(addFixture({ navigate, setLoader, submissionData })).then(
          () => {
            dispatch(getFixture({ id: zoneId, navigate, setLoader }));
            dispatch(setClearCourseData());
            dispatch(courseList({ navigate, setLoader, locationId }));
            resetForm();
            onHide();
          }
        );
      } else {
        // console.log('Edit Mode');

        dispatch(
          editFixture({ navigate, setLoader, submissionData, zoneId })
        ).then(() => {
          dispatch(getFixture({ id: zoneId, navigate, setLoader }));
          dispatch(setClearCourseData());
          dispatch(courseList({ navigate, setLoader, locationId }));
          resetForm();
          onHide();
        });
      }
    }
  };

  // Error Code
  // Function to clear specific error messages
  // const clearError = (uniqueKey, key) => {
  //   setErrors((prevErrors) => {
  //     const newErrors = { ...prevErrors };
  //     const fixture = formData.fixtures.find(
  //       (fixture) => fixture.unique_key === uniqueKey
  //     );
  //     if (fixture && fixture[key].trim() !== "") {
  //       delete newErrors[`${fixture.fixture_id}-${uniqueKey}-${key}`];
  //     }
  //     return newErrors;
  //   });
  // };

  return (
    <Modal
      show={show}
      onHide={onHide}
      backdrop="static"
      animation={true}
      size="xl"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {mode === "add" ? "Add Points of Interest" : "Edit Points of Interest"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="fixture_modal_body">
        <form onSubmit={onSubmit}>
          <div className="d-flex align-items-center justify-content-between pb-3">
            <h3 className="zone_heading">
              {/* How many safety prompts of each type are in this zone? */}
              Select the points of interest in this area.
            </h3>
            <button
              type="submit"
              className="submit_btn"
              disabled={totalFixtureCount === 0 && mode === "add"}
            >
              Save
            </button>
          </div>
          <div className="table-responsive fixTableResponsive pr-3">
            <table className="table fixTable">
              <thead>
                <tr>
                  <th scope="col" className="fixTable_heading_col"></th>
                  <th scope="col" className="fixTable_heading_col">
                    Count
                  </th>
                  <th scope="col" className="fixTable_heading_col">
                    Reference Name
                  </th>
                  <th scope="col" className="third_col fixTable_heading_col">
                    Serial Number
                  </th>
                </tr>
              </thead>
              <tbody>
                {/* {fixtureData.map((data, dataIndex) => ( */}
                {/* Check Assembly Point If true thn show August */}
                {fixtureData
                  .filter((data) => !data.extras.assembly_point)
                  .map((data, dataIndex) => (
                    <>
                      <tr
                        key={data.id}
                      // 18 OCT
                      // className={`${typeof ZoneAssembly === "undefined" && ['primary-exit', 'fire-alarm-pull', 'secondary-exit'].includes(data.slug) ? "opacity05" : ""
                      //   }`}
                      >
                        <td className="fixture_table w-20">
                          <div className="fixture_first_name">
                            <div className="fixture_first_img bg-transparent">
                              <img
                                src={
                                  data?.icon
                                    ? process.env.REACT_APP_MEDIA_URL_ORG +
                                    data?.icon
                                    : primaryImg
                                }
                                alt=""
                              />
                            </div>
                            <h2 className="fixture_first_text">{data?.name}</h2>
                          </div>
                        </td>
                        <td className="fixture_table w-15">
                          <div className="fixture_first_count">
                            <input
                              type="number"
                              className="count-input"
                              // 18 OCT
                              // disabled={typeof ZoneAssembly === "undefined" && ['primary-exit', 'fire-alarm-pull', 'secondary-exit'].includes(data.slug)}
                              value={
                                formData.fixtures.filter(
                                  (fixture) => fixture.fixture_id === data.id
                                ).length
                              }
                              onChange={(e) =>
                                handleInputChange(
                                  data,
                                  parseInt(e.target.value)
                                )
                              }
                            />
                          </div>
                        </td>
                        <td colSpan="2" className="fixture_table">
                          <div className="fixture_first_inputs">
                            {formData.fixtures
                              .filter(
                                (fixture) => fixture.fixture_id === data.id
                              )
                              .map((fixture) => (
                                <div
                                  className={`fixture_first_inputs_group ${formData.fixtures.filter(
                                    (fixture) =>
                                      fixture.fixture_id === data.id
                                  ).length > 1
                                    ? "mb-4"
                                    : ""
                                    }`}
                                  key={fixture.unique_key}
                                >
                                  <div className="fixture_first_reference">
                                    <div className="form-group">
                                      <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Exit near bathroom"
                                        value={fixture.reference_name}
                                        onChange={(e) =>
                                          handleFixtureChange(
                                            fixture.unique_key,
                                            "reference_name",
                                            e.target.value
                                          )
                                        }
                                      // Error Code
                                      // onBlur={() =>
                                      //   clearError(
                                      //     fixture.unique_key,
                                      //     "reference_name"
                                      //   )
                                      // }
                                      />
                                      {errors[
                                        `${fixture.fixture_id}-${fixture.unique_key}-reference_name`
                                      ] && (
                                          <div className="error ref_error">
                                            {
                                              errors[
                                              `${fixture.fixture_id}-${fixture.unique_key}-reference_name`
                                              ]
                                            }
                                          </div>
                                        )}
                                    </div>
                                  </div>
                                  <div className="fixture_first_serial ml-4">
                                    <div className="form-group">
                                      <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Type here"
                                        value={fixture.serial_number}
                                        onChange={(e) =>
                                          handleFixtureChange(
                                            fixture.unique_key,
                                            "serial_number",
                                            e.target.value
                                          )
                                        }
                                      // Error Code
                                      // onBlur={() =>
                                      //   clearError(
                                      //     fixture.unique_key,
                                      //     "serial_number"
                                      //   )
                                      // }
                                      />
                                      {errors[
                                        `${fixture.fixture_id}-${fixture.unique_key}-serial_number`
                                      ] && (
                                          <div className="error ref_error">
                                            {
                                              errors[
                                              `${fixture.fixture_id}-${fixture.unique_key}-serial_number`
                                              ]
                                            }
                                          </div>
                                        )}
                                    </div>
                                  </div>
                                  {mode === "add"
                                    ? ""
                                    : fixture?.id && (
                                      // <img
                                      //   src={deleteFixture}
                                      //   alt=""
                                      //   className={`deleteImg cursor ${data?.extras?.primary_exit || data?.extras?.assembly_point ? 'disabled' : ''}`}
                                      //   onClick={() => {
                                      //     if (!data?.extras?.primary_exit && !data?.extras?.assembly_point) {
                                      //       removeFixtureData(fixture?.id);
                                      //     }
                                      //   }}
                                      // />
                                      <img
                                        src={deleteFixture}
                                        alt=""
                                        className={`deleteImg cursor ${data?.extras?.assembly_point
                                          ? "disabled"
                                          : ""
                                          }`}
                                        onClick={() => {
                                          if (!data?.extras?.assembly_point) {
                                            removeFixtureData(fixture?.id);
                                          }
                                        }}
                                      // Old Code Primary,Secondary Dont Dleted
                                      // className={`deleteImg cursor ${data?.extras?.primary_exit || data?.extras?.secondary_exit || data?.extras?.assembly_point ? 'disabled' : ''}`}
                                      // onClick={() => {
                                      //   if (!data?.extras?.primary_exit && !data?.extras?.secondary_exit && !data?.extras?.assembly_point) {
                                      //     removeFixtureData(fixture?.id);
                                      //   }
                                      // }}
                                      />
                                    )}
                                </div>
                              ))}
                          </div>
                        </td>
                      </tr>
                      {/* {typeof ZoneAssembly === "undefined" && ['primary-exit', 'fire-alarm-pull', 'secondary-exit'].includes(data.slug) && (
                        <div className="error">
                          First Add Assembly Point
                        </div>
                      )} */}
                    </>
                  ))}
              </tbody>
            </table>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default FixtureModal;